// The llama page's state as plain data, and what it shows at a given moment.
// The room sends each change as it happens in the game. The viewer's stream
// shows it `delayMs` later, so the page waits that long before it shows one,
// and stays in step with what the viewer sees.

import type {
  Activity,
  BoardRow,
  Mood,
  Phase,
  RunLine,
  RunOutcome,
  RunPhase,
  Standing,
  ToAny,
  ToLlama,
} from '../protocol';

export type Link = 'connecting' | 'open' | 'reconnecting' | 'closed';
export type Ended = 'kicked' | 'full' | 'gone' | 'old' | 'closed' | 'room_locked' | 'cleared';

export interface StageNews {
  stage: number;
  phase: Phase;
  deadline: number | null;
  // Room time at which the viewer's stream shows this.
  showAt: number;
}

export interface ResultNews {
  stage: number;
  actual: number;
  guess: number;
  demerit: number;
  showAt: number;
}

export interface RunNews {
  n: number;
  phase: RunPhase;
  outcome: RunOutcome | null;
  stage: number | null;
  mine: RunLine;
  showAt: number;
}

export interface TipNews {
  stage: number;
  share: number;
  showAt: number;
}

export interface LlamaState {
  link: Link;
  welcomed: boolean;
  // The room's clock minus this browser's, in ms.
  offsetMs: number;
  delayMs: number;
  seat: number | null;
  place: number | null;
  // Room time at which this llama took its seat, or null without one. Its
  // projector appears on stream `delayMs` later.
  seatedAt: number | null;
  // The public number and the colour. Null until the room says.
  id: number | null;
  hue: number | null;
  // The name the room keeps for this llama, or empty.
  name: string;
  // The party activity that is on, or null.
  activity: Activity | null;
  mood: { mood: number | null; votes: number };
  myVote: Mood | null;
  // Room time at which this llama may rate its employer again.
  voteOpensAt: number;
  board: BoardRow[];
  standing: Standing | null;
  tips: TipNews[];
  news: StageNews[];
  locked: { stage: number; value: number } | null;
  result: ResultNews | null;
  // The Countant's run as the room last told it, or null before it says.
  run: RunNews | null;
  bootedStage: number | null;
  paused: boolean;
  hostOnline: boolean;
  ended: Ended | null;
  // The Turnstile site key while the room asks this llama to prove it is a
  // person, or null.
  challenge: string | null;
}

const NEWS_KEPT = 4;

export const freshState = (): LlamaState => ({
  link: 'connecting',
  welcomed: false,
  offsetMs: 0,
  delayMs: 0,
  seat: null,
  place: null,
  seatedAt: null,
  id: null,
  hue: null,
  name: '',
  activity: null,
  mood: { mood: null, votes: 0 },
  myVote: null,
  voteOpensAt: 0,
  board: [],
  standing: null,
  tips: [],
  news: [],
  locked: null,
  result: null,
  run: null,
  bootedStage: null,
  paused: false,
  hostOnline: true,
  ended: null,
  challenge: null,
});

// `localNow` is this browser's clock when the message arrived.
export function applyMessage(
  state: LlamaState,
  msg: ToLlama | ToAny,
  localNow: number,
): LlamaState {
  const roomNow = localNow + state.offsetMs;
  switch (msg.t) {
    case 'welcome':
      return {
        ...state,
        welcomed: true,
        challenge: null,
        offsetMs: msg.now - localNow,
        delayMs: msg.delayMs,
      };
    case 'config':
      return { ...state, delayMs: msg.delayMs };
    case 'you': {
      const seatedAt = msg.seat === null ? null : state.seat === null ? roomNow : state.seatedAt;
      const { seat, place, id, hue, name } = msg;
      return { ...state, seat, place, id, hue, name, seatedAt };
    }
    case 'activity':
      return { ...state, activity: msg.activity };
    case 'mood':
      return { ...state, mood: { mood: msg.mood, votes: msg.votes } };
    case 'voted':
      return { ...state, myVote: msg.value, voteOpensAt: msg.nextAt };
    case 'board':
      return { ...state, board: msg.rows };
    case 'standing':
      return { ...state, standing: msg.standing };
    case 'tipped': {
      const tip: TipNews = { ...msg, showAt: roomNow + state.delayMs };
      return { ...state, tips: [...state.tips, tip].slice(-NEWS_KEPT) };
    }
    case 'stage': {
      const item: StageNews = {
        stage: msg.stage,
        phase: msg.phase,
        deadline: msg.deadline,
        showAt: msg.at + state.delayMs,
      };
      const news = [...state.news, item].slice(-NEWS_KEPT);
      if (msg.phase !== 'counting') return { ...state, news };
      // Each run numbers its stages from 1 again. A stage that opens drops a
      // lock or a result left from a stage of the same number.
      const locked = state.locked?.stage === msg.stage ? null : state.locked;
      const result = state.result?.stage === msg.stage ? null : state.result;
      return { ...state, news, locked, result };
    }
    case 'locked':
      return { ...state, locked: { stage: msg.stage, value: msg.value } };
    case 'booted':
      return { ...state, bootedStage: msg.stage };
    case 'result':
      return { ...state, result: { ...msg, showAt: roomNow + state.delayMs } };
    case 'run': {
      const { t: _t, at, ...run } = msg;
      return { ...state, run: { ...run, showAt: at + state.delayMs } };
    }
    case 'paused':
      return { ...state, paused: msg.paused };
    case 'host':
      return { ...state, hostOnline: msg.online };
    case 'challenge':
      return { ...state, challenge: msg.siteKey };
    case 'kicked':
    case 'full':
    case 'gone':
    case 'old':
    case 'closed':
    case 'room_locked':
    case 'cleared':
      return { ...state, ended: msg.t };
    default:
      return state;
  }
}

// The newest stage news the viewer's stream has reached by `roomNow`.
export function shownStage(state: LlamaState, roomNow: number): StageNews | null {
  let shown: StageNews | null = null;
  for (const item of state.news) {
    if (item.showAt <= roomNow) shown = item;
  }
  return shown;
}

// The stage a guess is for: the room's newest, if it still takes guesses.
export function guessStage(state: LlamaState): number | null {
  const latest = state.news[state.news.length - 1];
  if (!latest || latest.phase === 'done') return null;
  return latest.stage;
}

export type Screen =
  | { kind: 'ended'; why: Ended }
  // A party activity holds the glass while it is on. The activity's own
  // screen is in `activities/`.
  | { kind: 'activity'; seat: number; activity: Activity }
  | { kind: 'connecting' }
  | { kind: 'challenge'; siteKey: string }
  | { kind: 'queue'; place: number | null; booted: number | null }
  | { kind: 'waiting'; seat: number; onStream: boolean }
  // Between stages, or, with `starting`, between the runs.
  | { kind: 'hold'; seat: number; starting: boolean }
  | { kind: 'run_over'; seat: number; outcome: RunOutcome; mine: RunLine }
  | { kind: 'counting'; seat: number; stage: number }
  | { kind: 'pad'; seat: number; stage: number; secondsLeft: number | null }
  | { kind: 'locked'; seat: number; value: number; secondsLeft: number | null }
  | { kind: 'result'; seat: number; actual: number; guess: number; demerit: number };

// How long the result stays on the glass before the hold screen comes up.
export const RESULT_SHOW_MS = 8000;

export function screenOf(state: LlamaState, localNow: number): Screen {
  if (state.ended) return { kind: 'ended', why: state.ended };
  if (!state.welcomed && state.challenge) return { kind: 'challenge', siteKey: state.challenge };
  if (!state.welcomed) return { kind: 'connecting' };
  const seat = state.seat;
  if (seat === null) return { kind: 'queue', place: state.place, booted: state.bootedStage };
  if (state.activity) return { kind: 'activity', seat, activity: state.activity };
  const roomNow = localNow + state.offsetMs;
  const onStream = state.seatedAt === null || state.seatedAt + state.delayMs <= roomNow;
  const shown = shownStage(state, roomNow);
  const result = state.result;
  const resultShown = result && shown && result.stage === shown.stage && result.showAt <= roomNow;
  if (resultShown && roomNow < result.showAt + RESULT_SHOW_MS) {
    const { actual, guess, demerit } = result;
    return { kind: 'result', seat, actual, guess, demerit };
  }
  // The run's news, once the stream shows it. A stage shown after it belongs
  // to that run. The stage a run's start or end finishes shares its time,
  // and belongs to the run before.
  const run = state.run && state.run.showAt <= roomNow ? state.run : null;
  const beforeRun = !shown || (run !== null && shown.showAt <= run.showAt);
  if (run?.phase === 'over' && beforeRun) {
    return { kind: 'run_over', seat, outcome: run.outcome ?? 'over', mine: run.mine };
  }
  if (!shown) return { kind: 'waiting', seat, onStream };
  if (run?.phase === 'playing' && beforeRun) return { kind: 'hold', seat, starting: true };
  // Past its time the result gives way to the hold, through the shop and the
  // map, until the next stage.
  if (resultShown) return { kind: 'hold', seat, starting: false };
  const secondsLeft =
    shown.phase === 'closed' && shown.deadline !== null
      ? Math.max(0, Math.ceil((shown.deadline - roomNow) / 1000))
      : null;
  const locked = state.locked?.stage === shown.stage ? state.locked : null;
  if (locked) return { kind: 'locked', seat, value: locked.value, secondsLeft };
  // A stage that ended with no guess from this seat: the viewer took the seat
  // after it closed. (One who missed the deadline is back in the queue.)
  if (shown.phase === 'done') return { kind: 'hold', seat, starting: false };
  // The sheep are still going by on the stream. The pad opens when the stream
  // shows the count is over, as the Countant's own pad does.
  if (shown.phase === 'counting') return { kind: 'counting', seat, stage: shown.stage };
  return { kind: 'pad', seat, stage: shown.stage, secondsLeft };
}

// The room's time now, by this browser's clock.
export const roomTime = (state: LlamaState, localNow: number): number => localNow + state.offsetMs;

// Whole seconds until this llama may vote again, or 0 when it may vote now.
export function voteWait(state: LlamaState, localNow: number): number {
  return Math.max(0, Math.ceil((state.voteOpensAt - roomTime(state, localNow)) / 1000));
}

// Tips the viewer's stream has reached by now, newest last.
export function shownTips(state: LlamaState, localNow: number): TipNews[] {
  const roomNow = roomTime(state, localNow);
  return state.tips.filter((tip) => tip.showAt <= roomNow);
}
